/**
 * Reading a PDF of printed music into a score: shapes of the `recognise` verb and its job,
 * of the readers there are to choose from, and of a reading counted against the true score.
 *
 * The reading is a machine's (Audiveris unless another reader is named; each is run by the
 * server as a program of its own), takes tens of seconds a page, and gets notes wrong. So it
 * is a job, followed page by page, and what comes back says in words that it has to be
 * checked against the page.
 */

import type { TheoryJob } from "./job";

/**
 * What `POST /theory/recognise` takes: the id `POST /theory/uploads` gave the PDF, and the
 * reader to read it, by its name in `GET /theory/readers`. Audiveris where none is named.
 */
export interface RecogniseRequest {
  upload: string;
  reader?: string;
}

/**
 * What `POST /theory/pictures` takes: the ids `POST /theory/uploads` gave photos and pictures
 * of pages, and any PDF among them, in the order their pages are to be in, and what each file
 * was called where it was picked. The reply is one PDF of their pages, read as any scan is.
 */
export interface PicturesRequest {
  uploads: string[];
  names: string[];
}

/** The most pictures `POST /theory/pictures` makes into one PDF. */
export const PICTURES_LIMIT = 200;

/**
 * How far a reading has got: the page a model that sees is looking at for the music
 * (`look`), then the reading (`scan`), each once the pages are counted.
 *
 * Audiveris reads a page at a time, several at once, and tells as each ends how many pages are
 * through (`done`: read, failed on, or with no music) and what it has read so far (`partial`),
 * numbered as each goes out (`partials`). Any other reader is handed the pages at once and
 * tells only the page it is beginning (`page`, in `scan`). `by` is what a sentence calls the
 * reader reading now: a request that names none may be read by one and then by Audiveris.
 * `GET /theory/recognise/:job` carries `partial` only to a caller that asks for one newer than
 * its own (`?since=`); `partials` is always there.
 */
export interface RecogniseProgress {
  stage: "look" | "scan";
  by?: string;
  page?: number;
  pages?: number;
  done?: number;
  partial?: RecognisePartial;
  partials?: number;
}

/** What a reading still under way has read so far, as its reply will say it. */
export type RecognisePartial = Omit<RecognisePayload, "seconds">;

/** The bars read off one page of the scan: 1-based positions in the score, both ends in it. */
export interface PageSpan {
  page: number;
  first: number;
  last: number;
}

export interface RecognisePayload {
  musicxml: string;
  /** What a reader has to be told about the score: that a machine read it, and how much. */
  note: string;
  /** The pages of the scan, those not read among them. */
  pages: number;
  /** The pages with no music on them, which no reader was shown, counted from 1. */
  skipped: number[];
  /** The pages of music the reader failed on, left out of `musicxml`, counted from 1. */
  unread: number[];
  /** The pages not yet read, of a reading still under way; none in the reply. */
  waiting: number[];
  /** Movements the reader found on the pages, every one of them in `musicxml`, in order. */
  movements: number;
  /** Notes with a pitch in `musicxml`. */
  notes: number;
  /** The reader that read it, by name. */
  reader: string;
  /** How long the reading took. */
  seconds: number;
  /**
   * The bars on each page that has any, in page order; `null` where the reading does not say
   * which page its bars came off, and they can only be shown all together.
   */
  spans: PageSpan[] | null;
}

/** What `GET /theory/recognise/:job` answers. */
export type RecogniseJob = TheoryJob<RecogniseProgress, RecognisePayload>;

/** One machine that reads printed music. */
export interface ScoreReader {
  name: string;
  label: string;
  /** Whether its readings may be arranged and kept; otherwise it is for the Read board only. */
  shipped: boolean;
  /** What it is, in a line. */
  about: string;
  /** What to say of it in a word or two where the server has not been given it. */
  absent: string;
  /** Why it cannot read on this server, or `null` where it can. */
  why: string | null;
}

/** What `GET /theory/readers` answers. */
export interface ReadersPayload {
  readers: ScoreReader[];
}

/**
 * What `POST /theory/count` takes: two uploads, the reading and the score it was read from,
 * both MusicXML. Each is taken by the count and gone when it answers.
 */
export interface CountRequest {
  read: string;
  truth: string;
}

/**
 * A reading counted against the true score, note for note.
 *
 * A note is found where the reading has its written pitch at its moment in the bar. The
 * page may hold more than the piece, so what the reader got right is counted over the
 * piece's stretch of the reading: bars `first` to `last`, `null` where it holds none of it.
 */
export interface CountPayload {
  /** Notes the true score strikes, and the bars they are in. */
  truthNotes: number;
  truthBars: number;
  /** Of those, the notes the reading has, and has with their length too. */
  found: number;
  withLength: number;
  first: number | null;
  last: number | null;
  /** Notes the reading strikes inside the stretch, and how many of them are the score's. */
  readNotes: number;
  right: number;
  /** Every bar of the reading in order: the notes it strikes, and how many are the score's. */
  bars: [notes: number, right: number][];
}
